import React, { useState, memo, useEffect, useId } from 'react';
import { editIconPurple } from '../../../../../../assets/hostedassets';
import { CellRendererProps } from '../../../DataTable.types';
import styles from './TextInputCellRenderer.module.scss';
import clsx from 'clsx';
import LoadingSpinner from '../../../../LoadingSpinner/LoadingSpinner';
import { isEmpty, truncateByCharacters } from '../../../../../../utils/utils';
import { KEYBOARD_EVENTS } from '../../../../../../utils/constants/commonConstants';

/**
 * TextInputCellRenderer — the editable counterpart to the read-only TextCellRenderer.
 * Same click-to-edit / save-on-blur-or-Enter contract as NumberCellRenderer and
 * DatePickerCellRenderer: dynamicProps.updateCustomFieldStatus(row, {value}, column, handlers)
 * writes the field, dynamicProps.isDisabled (driven by column.disable) keeps read-only
 * custom fields (e.g. backend-populated "Auto" columns) non-editable.
 */
export const TextInputCellRenderer: React.FC<CellRendererProps> = memo(({ column, dynamicProps, row, value }) => {
    const { updateCustomFieldStatus, isDisabled } = dynamicProps ?? {};
    const [isEditing, setIsEditing] = useState(false);
    const stringValue = value == null ? '' : String(value);
    const [editValue, setEditValue] = useState<string>(stringValue);
    const [loading, setLoading] = useState(false);
    const generatedId = useId();

    const hasValue = !isEmpty(value);

    useEffect(() => {
        if (!isEditing) {
            setEditValue(stringValue);
        }
    }, [stringValue, isEditing]);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDisabled) return;
        setIsEditing(true);
        setEditValue(stringValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditValue(e.target.value);
    };

    const handleInputBlur = () => {
        setIsEditing(false);
        const trimmedValue = editValue.trim();
        if (trimmedValue === stringValue) return;

        const handlers = {
            onStart: () => setLoading(true),
            onSuccess: () => setLoading(false),
            onError: () => setLoading(false),
        };
        updateCustomFieldStatus(row, { value: trimmedValue }, column, handlers);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === KEYBOARD_EVENTS.ENTER) {
            handleInputBlur();
        } else if (e.key === KEYBOARD_EVENTS.ESCAPE) {
            setIsEditing(false);
            setEditValue(stringValue);
        }
    };

    if (isEditing) {
        return (
            <div className={styles.textInputCellRenderer}>
                <div className={styles.inputContainer}>
                    <input
                        type="text"
                        id={`text-input-${row?.id || row?.index || generatedId}`}
                        name={`text-input-${column?.field || 'value'}`}
                        value={editValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onKeyDown={handleInputKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        className={styles.textInput}
                        autoFocus
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.textInputCellRenderer}>
            <div
                className={clsx(styles.textCellRendererValue, hasValue && styles.hasValue, isDisabled && styles.disabled)}
                onClick={handleClick}
                style={{ cursor: isDisabled ? 'default' : 'pointer' }}
            >
                {loading ? (
                    <LoadingSpinner color="var(--primary-purple)" />
                ) : hasValue ? (
                    <>
                        <span title={value} className={styles.valueText}>
                            {truncateByCharacters(String(value), 40)}
                        </span>
                        {!isDisabled && <img src={editIconPurple} alt="Edit" className={styles.editIcon} />}
                    </>
                ) : (
                    <>
                        <div className={styles.emptyValue}>-</div>
                        {!isDisabled && (
                            <div className={styles.addContainer}>
                                <span className={styles.addText}>Add</span>
                                <img src={editIconPurple} alt="Add" className={styles.editIcon} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
});
